import { useState } from "react";
import { connect, useDispatch } from "react-redux";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import Icon from '../../../../../../../components/Icon'
import DotsVertical from '../../../../../../../assets/icons/dots-vertical.svg';

type MenuItem = {
  label: string;
  icon: React.ReactNode;
  action: any;
  iconBgColor: string;
  labelBgColor: string;
  type: 'button' | 'link'
};

const Backdrop = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.2); // Podés ajustar la opacidad si querés que sea más sutil
  z-index: 100;
`;

const Wrapper = styled.div`
  position: relative;
  display: inline-block; // o flex si tenés varios elementos
`;

const MenuItemsWrapper = styled.div`
  position: absolute;
  top: 100%; // justo debajo del botón
  right: 0;
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  z-index: 200; // más que el botón
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  z-index: 120;
`;

const FabButton = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background-color: #E0DEF8;
  color: white;
  border: none;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MenuButton = styled(motion.button) <{ labelBgColor: string }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 9999px;
  background: ${({ labelBgColor }) => labelBgColor ? labelBgColor : "white"};
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  font-weight: 600;
  font-size: 16px;
  color: #2f2f2f;
  border: none;
  cursor: pointer;
`;

const MenuAnchor = styled(motion.a) <{ labelBgColor: string }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 9999px;
  background: ${({ labelBgColor }) => labelBgColor || "white"};
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  font-weight: 600;
  font-size: 16px;
  color: #2f2f2f;
  border: none;
  text-decoration: none;
  cursor: pointer;
`;

const IconWrapper = styled.div<{ iconBgColor: string }>`
  background-color: ${({ iconBgColor }) => iconBgColor};
  padding: 6px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FloatingMenu = ({
  menuItems,
}) => {
  const [open, setOpen] = useState(false);

  const handleItemClick = (item: MenuItem) => {
    if (item.type === 'button') {
      if (typeof item.action === 'function') {
        item.action();
      }
    } else if (item.type === 'link' && typeof item.action === 'string') {
      // Se deja vacío ya que el <a> se encarga de redireccionar
    }

    // Cerrar el menú en todos los casos
    setOpen(false);
  };

  return (
    <>
      {open && (
        <Backdrop
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setOpen(false)}
        />
      )}

      <Wrapper>
        <FabButton onClick={() => setOpen((prev) => !prev)}>
          <Icon icon={DotsVertical} color="black" size="24px" />
        </FabButton>

        <AnimatePresence>
          {open && (
            <MenuItemsWrapper>
              {menuItems.map((item, i) => {
                const animationProps = {
                  key: item.label,
                  initial: { opacity: 0, y: -10 },
                  animate: { opacity: 1, y: 0 },
                  exit: { opacity: 0, y: -10 },
                  transition: { delay: i * 0.05 },
                };

                const CommonProps = {
                  ...animationProps,
                  labelBgColor: item.labelBgColor,
                  onClick: () => handleItemClick(item),
                  style: { width: item?.width }
                };

                return item.type === 'link' ? (
                  <MenuAnchor
                    href={item.action}
                    target="_blank"
                    rel="noopener noreferrer"
                    {...CommonProps}
                  >
                    {item.label}
                    <IconWrapper iconBgColor={item.iconBgColor}>
                      {item.icon}
                    </IconWrapper>
                  </MenuAnchor>
                ) : (
                  <MenuButton {...CommonProps} >
                    {item.label}
                    <IconWrapper iconBgColor={item.iconBgColor}>
                      {item.icon}
                    </IconWrapper>
                  </MenuButton>
                );
              })}
            </MenuItemsWrapper>
          )}
        </AnimatePresence>
      </Wrapper>
    </>
  );
}

const state = ({ groupStore, categoryStore }) => {
  const { data: allCategories, states: allCategoriesState } = categoryStore.allCategories;
  const { data: group, states: groupStates } = groupStore.group;

  return {
    group,
    groupStates,
    allCategories,
    allCategoriesState,
  };
};

export default connect(state)(FloatingMenu);
